import { LazyQueryHookOptions, useLazyQuery } from '@apollo/client'
import getStoresQuery from './query'
import { IGetStoresResponse, IGetStoresVariables } from './types'

/**
 * Lazy `getStores`, used to resolve one depot code to its display name.
 *
 * **`cache-first`, and stated rather than inherited.** It is Apollo's default,
 * so writing it changes no behaviour — what it changes is that the next reader
 * can see it was chosen. A depot's name does not move within a webview
 * session, which is not true of anything else these order documents read:
 * `useGetOrderDetail` and `useOrderConfirmation` take `network-only` because
 * the order changes under them, `useGetOrders` takes it so a filter change or
 * a retry always hits the API, and both order mutations take `no-cache`.
 * `useLazyGetLastOrder` is the one order-shaped read already on this policy —
 * by inheriting the default rather than by choosing it, which is the gap this
 * line closes.
 *
 * `useLazyAlternativeProducts` is `no-cache` too and is *not* a precedent
 * either way: it is a recommendations document, and its reason is that it is
 * SKU-keyed over prices and promotions.
 *
 * The cache is what makes re-entering an order, or opening a second order from
 * the same depot, cost no request. Note it dedupes on the *arguments*, not on
 * an entity id: `config/apolloClient.ts` builds its `InMemoryCache` with
 * `addTypename: false`, so nothing here normalises and the result is held
 * against the root field's arguments. That is sufficient for this lookup and
 * not something to build on.
 * @internal
 */
const useLazyGetStores = (
  options?: LazyQueryHookOptions<IGetStoresResponse, IGetStoresVariables>,
) =>
  useLazyQuery<IGetStoresResponse, IGetStoresVariables>(getStoresQuery, {
    fetchPolicy: 'cache-first',
    ...options,
  })

export { useLazyGetStores }
